import type { Asset } from '@compass/client/types/asset';
import { CallStatus } from '@compass/client/types/call';
import { formatDuration } from '@utils/date';
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { TranscriptSegment } from '@/hooks/useTranscript';
import { useTranscript } from '@/hooks/useTranscript';
import type { OrgProgramContextOverride } from '@/types/context';
import {
  findTextMatches,
  findTranscriptSegmentIndexForTime,
  normalizeSearchMatchIndex,
  type TextMatch,
} from '@/utils/transcript';

/** Empty state component for transcript */
export const TranscriptEmptyState: React.FC<{
  message: string;
  description?: string;
  icon?: React.ReactNode;
}> = ({ message, description, icon }) => (
  <div className="flex flex-col items-center justify-center text-muted-foreground">
    {icon || (
      <svg
        className="w-12 h-12 mb-3 text-muted-foreground"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    )}
    <p className="text-sm font-medium">{message}</p>
    {description && <p className="text-xs text-muted-foreground mt-1 text-center">{description}</p>}
  </div>
);

interface TranscriptProps {
  caseRecordId: string;
  callId: string;
  callSid?: string;
  className?: string;
  currentTime?: number;
  onSegmentClick?: (time: number) => void;
  onTranscriptLoad?: (segments: TranscriptSegment[]) => void;
  callStatus?: CallStatus;
  pollInterval?: number;
  /** Transcript assets - can be empty array if no assets available */
  assets: Asset[];
  /** Whether assets are still loading */
  isLoadingAssets?: boolean;
  /** Optional org/program override for org-scoped pages like QC details */
  contextOverride?: OrgProgramContextOverride;
  /** Enables built-in transcript search UI with highlight and navigation */
  enableSearch?: boolean;
  /**
   * Externally-controlled search term (BI-1246). When provided, it drives the
   * highlight instead of the internal search box — used to highlight one query
   * across many transcripts at once (e.g. the Human Audit transcripts pane).
   * The built-in search UI stays hidden unless `enableSearch` is also set.
   */
  externalSearchQuery?: string;
  /** Range of seconds to highlight (from evidence time_start to time_end) */
  evidenceHighlightRange?: { start: number; end: number; segmentIndex?: number } | null;
  /** Callback to clear the evidence highlight range */
  onEvidenceHighlightClear?: () => void;
}

interface RenderSearchMatchOptions {
  text: string;
  matches: TextMatch[];
  globalMatchStartIndex: number;
  activeSearchMatchIndex: number | null;
  getMatchRef: (index: number) => (element: HTMLElement | null) => void;
}

const renderSearchMatches = ({
  text,
  matches,
  globalMatchStartIndex,
  activeSearchMatchIndex,
  getMatchRef,
}: RenderSearchMatchOptions) => {
  if (matches.length === 0) {
    return text;
  }

  const nodes: React.ReactNode[] = [];
  let cursor = 0;

  matches.forEach((match, index) => {
    const globalMatchIndex = globalMatchStartIndex + index;
    const isActiveMatch = globalMatchIndex === activeSearchMatchIndex;

    if (cursor < match.start) {
      nodes.push(
        <React.Fragment key={`text-${globalMatchIndex}-${cursor}`}>
          {text.slice(cursor, match.start)}
        </React.Fragment>,
      );
    }

    nodes.push(
      <mark
        key={`match-${globalMatchIndex}-${match.start}`}
        ref={getMatchRef(globalMatchIndex)}
        data-search-match-index={globalMatchIndex}
        data-active-search-match={isActiveMatch ? 'true' : 'false'}
        aria-current={isActiveMatch ? 'true' : undefined}
        className={`${
          isActiveMatch
            ? 'bg-search-highlight-active text-search-highlight-active-foreground'
            : 'bg-search-highlight text-search-highlight-foreground'
        }`}
      >
        {text.slice(match.start, match.end)}
      </mark>,
    );

    cursor = match.end;
  });

  if (cursor < text.length) {
    nodes.push(<React.Fragment key={`text-tail-${cursor}`}>{text.slice(cursor)}</React.Fragment>);
  }

  return nodes;
};

export const Transcript: React.FC<TranscriptProps> = ({
  caseRecordId,
  callId,
  callSid,
  className = '',
  currentTime = 0,
  onSegmentClick,
  onTranscriptLoad,
  callStatus,
  pollInterval = 2000,
  assets,
  isLoadingAssets = false,
  contextOverride,
  enableSearch = false,
  externalSearchQuery,
  evidenceHighlightRange,
  onEvidenceHighlightClear,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const activeSegmentRef = useRef<HTMLDivElement>(null);
  const firstHighlightedRef = useRef<HTMLDivElement>(null);
  const searchMatchRefs = useRef<(HTMLElement | null)[]>([]);
  const searchMatchRefCallbacks = useRef(new Map<number, (element: HTMLElement | null) => void>());
  const previousActiveIndex = useRef<number>(-1);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousTranscriptLengthRef = useRef<number>(0);
  const lastSegmentRef = useRef<HTMLDivElement>(null);
  const scrollPositionBeforeUpdateRef = useRef<{ wasAtBottom: boolean; scrollTop: number }>({
    wasAtBottom: true,
    scrollTop: 0,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchMatchIndex, setActiveSearchMatchIndex] = useState<number | null>(null);

  // Find the latest transcript asset from the provided assets
  const transcriptAsset = assets.length > 0 ? assets[0].id : undefined;

  // Use the transcript hook with the asset ID (only if we have an asset)
  const { transcript, isLoading: isLoadingTranscript } = useTranscript(
    caseRecordId,
    transcriptAsset,
    {
      callStatus,
      pollInterval,
      callSid,
      contextOverride,
    },
  );

  // Combined loading state: either assets are loading OR transcript is loading
  const isLoading = isLoadingAssets || isLoadingTranscript;
  // An external query (parent-driven, cross-transcript highlight) takes precedence
  // over the internal search box when provided.
  const normalizedSearchQuery = (
    externalSearchQuery !== undefined ? externalSearchQuery : enableSearch ? searchQuery : ''
  ).trim();

  const transcriptSearchState = useMemo(() => {
    const matches: Array<TextMatch & { segmentIndex: number }> = [];
    const matchesBySegment = new Map<number, TextMatch[]>();
    const globalMatchStartBySegment = new Map<number, number>();

    transcript.forEach((segment, index) => {
      const segmentMatches = findTextMatches(segment.text, normalizedSearchQuery);
      if (segmentMatches.length === 0) {
        return;
      }

      globalMatchStartBySegment.set(index, matches.length);
      matchesBySegment.set(index, segmentMatches);
      segmentMatches.forEach(match => matches.push({ ...match, segmentIndex: index }));
    });

    return {
      matches,
      matchesBySegment,
      globalMatchStartBySegment,
    };
  }, [normalizedSearchQuery, transcript]);

  const normalizedActiveSearchMatchIndex = useMemo(
    () => normalizeSearchMatchIndex(activeSearchMatchIndex, transcriptSearchState.matches.length),
    [activeSearchMatchIndex, transcriptSearchState.matches.length],
  );
  const currentSearchResult =
    normalizedActiveSearchMatchIndex == null ? 0 : normalizedActiveSearchMatchIndex + 1;
  const hasSearchQuery = normalizedSearchQuery.length > 0;

  // External-query mode (parent-driven, cross-transcript search): point the active
  // match at the first hit whenever the query or its match set changes, so opening
  // a transcript scrolls straight to the first match. User up/down navigation isn't
  // affected — it changes activeSearchMatchIndex without changing these deps.
  useEffect(() => {
    if (externalSearchQuery === undefined) return;
    setActiveSearchMatchIndex(
      normalizedSearchQuery && transcriptSearchState.matches.length > 0 ? 0 : null,
    );
  }, [externalSearchQuery, normalizedSearchQuery, transcriptSearchState.matches.length]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setActiveSearchMatchIndex(null);
  }, []);

  const clearSearch = useCallback(() => {
    handleSearchChange('');
    searchInputRef.current?.focus();
  }, [handleSearchChange]);

  const navigateSearchMatches = useCallback(
    (direction: 'next' | 'prev') => {
      if (transcriptSearchState.matches.length === 0) {
        return;
      }

      setActiveSearchMatchIndex(currentIndex => {
        const safeIndex =
          normalizeSearchMatchIndex(currentIndex, transcriptSearchState.matches.length) ?? 0;
        const delta = direction === 'next' ? 1 : -1;
        return (
          (safeIndex + delta + transcriptSearchState.matches.length) %
          transcriptSearchState.matches.length
        );
      });
    },
    [transcriptSearchState.matches.length],
  );

  const handleSearchKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        clearSearch();
        return;
      }

      if (event.key !== 'Enter') {
        return;
      }

      event.preventDefault();
      navigateSearchMatches(event.shiftKey ? 'prev' : 'next');
    },
    [clearSearch, navigateSearchMatches],
  );

  const getSearchMatchRef = useCallback((index: number) => {
    const existingCallback = searchMatchRefCallbacks.current.get(index);
    if (existingCallback) {
      return existingCallback;
    }

    const callback = (element: HTMLElement | null) => {
      searchMatchRefs.current[index] = element;
    };

    searchMatchRefCallbacks.current.set(index, callback);
    return callback;
  }, []);

  // Helper function to check if container is at bottom
  const isAtBottom = (container: HTMLElement): boolean => {
    const scrollBottom = container.scrollTop + container.clientHeight;
    const threshold = 100; // Auto-scroll if within 100px of bottom
    return scrollBottom >= container.scrollHeight - threshold;
  };

  // Track scroll position continuously
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const atBottom = isAtBottom(container);
      const scrollTop = container.scrollTop;

      // Update the ref with current scroll state
      scrollPositionBeforeUpdateRef.current = {
        wasAtBottom: atBottom,
        scrollTop: scrollTop,
      };
    };

    // Check initial position
    handleScroll();

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [callId]); // Re-attach when callId changes

  // Auto-scroll to bottom when new transcript content arrives
  useEffect(() => {
    let rafId: number | undefined;

    // Skip if it's the initial load
    if (previousTranscriptLengthRef.current === 0) {
      previousTranscriptLengthRef.current = transcript.length;
      // Set initial scroll position for new transcript
      scrollPositionBeforeUpdateRef.current.wasAtBottom = true;
      return;
    }

    // Check if new content was added
    if (transcript.length > previousTranscriptLengthRef.current) {
      if (scrollPositionBeforeUpdateRef.current.wasAtBottom) {
        // Use requestAnimationFrame to ensure DOM is painted before scrolling
        rafId = requestAnimationFrame(() => {
          const container = scrollContainerRef.current;
          if (container) {
            container.scrollTo({
              top: container.scrollHeight,
              behavior: 'smooth',
            });
          }
        });
      }
    }

    previousTranscriptLengthRef.current = transcript.length;

    return () => {
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [transcript]);

  // Reset refs and scroll position when call changes
  useEffect(() => {
    previousActiveIndex.current = -1;
    previousTranscriptLengthRef.current = 0;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }

    // Scroll transcript to top when switching calls
    requestAnimationFrame(() => {
      const container = scrollContainerRef.current;
      if (container) {
        container.scrollTop = 0;
      }
    });
  }, [callId]);

  useEffect(() => {
    setSearchQuery('');
    setActiveSearchMatchIndex(null);
  }, [callId, caseRecordId]);

  useEffect(() => {
    if (enableSearch) {
      return;
    }

    setSearchQuery('');
    setActiveSearchMatchIndex(null);
  }, [enableSearch]);

  // Notify parent when transcript loads
  useEffect(() => {
    onTranscriptLoad?.(transcript);
  }, [transcript, onTranscriptLoad]);

  useEffect(
    () => () => {
      searchMatchRefs.current = [];
      searchMatchRefCallbacks.current.clear();
    },
    [],
  );

  useEffect(() => {
    if (normalizedSearchQuery) {
      return;
    }

    // This cache is intentionally fully reset on query clear, so searches do not
    // accumulate callbacks across sessions even if users repeatedly type/delete.
    searchMatchRefs.current = [];
    searchMatchRefCallbacks.current.clear();
  }, [normalizedSearchQuery]);

  useEffect(() => {
    searchMatchRefs.current = searchMatchRefs.current.slice(
      0,
      transcriptSearchState.matches.length,
    );
    // While a query is active, the callback cache is bounded by match count.
    // Lower indices are reused for the current render; only stale tail entries are pruned.
    searchMatchRefCallbacks.current.forEach((_, index) => {
      if (index >= transcriptSearchState.matches.length) {
        searchMatchRefCallbacks.current.delete(index);
      }
    });
  }, [transcriptSearchState.matches.length]);

  /**
   * Centre a segment **inside the transcript's own scroll container**, touching nothing
   * above it (BV-2129).
   *
   * `scrollIntoView({ block: 'center' })` cannot do this: per spec it scrolls EVERY
   * scrollable ancestor plus the viewport, and it cannot no-op when the target is
   * already visible. The transcript pane and the Call Script column are siblings in the
   * Review row, under a shared `overflow-hidden` ancestor — which is still a scroll
   * container as far as scrollIntoView is concerned. So centring a cited line re-centred
   * that ancestor too and shifted the whole row, which reads as the call script
   * scrolling on its own when the rep clicks an evidence button.
   *
   * Setting `scrollTop` on the container directly is the only variant that guarantees no
   * ancestor moves. `offsetTop` is relative to the nearest positioned ancestor, so it is
   * measured against the container's own box rather than assumed.
   */
  const centerInTranscript = useCallback((el: HTMLElement | null) => {
    const container = scrollContainerRef.current;
    if (!el || !container) return;
    const top =
      el.offsetTop - container.offsetTop - container.clientHeight / 2 + el.clientHeight / 2;
    container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (normalizedActiveSearchMatchIndex == null) {
      return;
    }

    // Re-run when the query changes so typing always jumps back to the first visible match.
    // Same containment as the evidence jump: search-match navigation used
    // `block: 'center'`, which re-centred the shared Review-row ancestor and shifted the
    // Call Script column alongside it.
    const frameId = requestAnimationFrame(() =>
      centerInTranscript(searchMatchRefs.current[normalizedActiveSearchMatchIndex] ?? null),
    );

    return () => cancelAnimationFrame(frameId);
  }, [normalizedActiveSearchMatchIndex, normalizedSearchQuery, centerInTranscript]);

  // Find active segment first before using it in effects
  const activeSegmentIndex =
    currentTime > 0 ? findTranscriptSegmentIndexForTime(transcript, currentTime) : -1;

  // Compute highlighted segment indices from evidence range
  const highlightedSegmentIndices = useMemo(() => {
    if (!evidenceHighlightRange) return new Set<number>();
    if (
      evidenceHighlightRange.segmentIndex !== undefined &&
      evidenceHighlightRange.segmentIndex >= 0 &&
      evidenceHighlightRange.segmentIndex < transcript.length
    ) {
      return new Set([evidenceHighlightRange.segmentIndex]);
    }

    const { start, end } = evidenceHighlightRange;
    const indices = new Set<number>();
    transcript.forEach((segment, index) => {
      // A segment overlaps the range if it starts before range ends AND ends after range starts
      if (segment.start < end && segment.end > start) {
        indices.add(index);
      }
    });
    return indices;
  }, [evidenceHighlightRange, transcript]);

  // Pre-compute segment-to-index map for O(1) lookups in the render loop
  const segmentToOriginalIndex = useMemo(() => {
    const map = new Map<TranscriptSegment, number>();
    transcript.forEach((segment, index) => map.set(segment, index));
    return map;
  }, [transcript]);

  const firstHighlightedIndex = useMemo(() => {
    if (highlightedSegmentIndices.size === 0) return -1;
    return Math.min(...highlightedSegmentIndices);
  }, [highlightedSegmentIndices]);

  // Centre the first highlighted segment when the evidence range changes — within the
  // transcript only. See centerInTranscript for why this is not scrollIntoView.
  useEffect(() => {
    if (firstHighlightedIndex < 0 || !firstHighlightedRef.current) return;
    const timeout = setTimeout(() => centerInTranscript(firstHighlightedRef.current), 100);
    return () => clearTimeout(timeout);
  }, [firstHighlightedIndex, evidenceHighlightRange, centerInTranscript]);

  // Clear evidence highlight when audio playback moves past the range.
  // 1-second buffer prevents premature clearing from timestamp precision mismatches.
  useEffect(() => {
    if (evidenceHighlightRange && currentTime > evidenceHighlightRange.end + 1) {
      onEvidenceHighlightClear?.();
    }
  }, [currentTime, evidenceHighlightRange, onEvidenceHighlightClear]);

  // Auto-scroll to active transcript segment with debouncing (audio playback)
  useEffect(() => {
    if (normalizedSearchQuery) {
      return;
    }

    // Only scroll when segment actually changes
    if (activeSegmentIndex !== previousActiveIndex.current && activeSegmentIndex >= 0) {
      previousActiveIndex.current = activeSegmentIndex;

      // Clear any pending scroll
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Debounce the scroll to prevent flakiness
      scrollTimeoutRef.current = setTimeout(() => {
        if (activeSegmentRef.current) {
          activeSegmentRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        }
      }, 100); // Small delay to batch rapid changes
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [activeSegmentIndex, normalizedSearchQuery]); // Only trigger on segment change

  // Determine if we should show empty state (no assets available or empty transcript)
  const hasNoAssets = assets.length === 0 && !isLoadingAssets;
  const hasEmptyTranscript = !hasNoAssets && transcript.length === 0 && !isLoading;
  const activeSegment = activeSegmentIndex >= 0 ? transcript[activeSegmentIndex] : undefined;

  return (
    // The test hook for the transcript surface. BV-1575 moved it behind the Review
    // sidebar's section switcher, and the e2e suite had no way to scope an assertion to
    // the pane itself — only to page-wide text (BV-2120).
    <div className={`flex h-full min-h-0 flex-col ${className}`} data-testid="transcript-pane">
      {(enableSearch || (externalSearchQuery !== undefined && hasSearchQuery)) && (
        <div className="border-b px-4 py-3" role="search">
          <div className="flex items-center gap-3">
            {/* Internal search input — hidden in external-query mode (the parent
                owns the query; only the match navigation is shown). */}
            {enableSearch && (
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  ref={searchInputRef}
                  value={searchQuery}
                  onChange={event => handleSearchChange(event.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search transcript..."
                  className="h-10 w-full rounded-xl border border-input bg-background pl-10 pr-10 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                {hasSearchQuery && (
                  <button
                    type="button"
                    aria-label="Clear transcript search"
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}

            {hasSearchQuery && (
              <div className="flex shrink-0 items-center gap-1.5 text-muted-foreground text-sm">
                <span
                  className="min-w-[4rem] text-right tabular-nums"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {currentSearchResult} of {transcriptSearchState.matches.length}
                </span>
                <button
                  type="button"
                  aria-label="Previous transcript match"
                  onClick={() => navigateSearchMatches('prev')}
                  disabled={transcriptSearchState.matches.length === 0}
                  className="rounded-md p-1 transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  aria-label="Next transcript match"
                  onClick={() => navigateSearchMatches('next')}
                  disabled={transcriptSearchState.matches.length === 0}
                  className="rounded-md p-1 transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div
        ref={scrollContainerRef}
        className="transcript-container flex-1 min-h-0 overflow-y-auto py-4"
      >
        {isLoading ? (
          <div className="px-4">
            {[...Array(3)].map((_, index) => {
              const isLeft = index % 3 === 0;
              return (
                <div
                  key={index}
                  className={`flex ${isLeft ? 'justify-start' : 'justify-end'} mb-2`}
                >
                  <div className="max-w-[85%]">
                    <div className="animate-pulse">
                      <div
                        className={`flex items-center gap-2 mb-1 ${isLeft ? '' : 'justify-end'}`}
                      >
                        <div className="h-2 bg-muted rounded w-16" />
                        <div className="h-2 bg-muted rounded w-10" />
                      </div>
                      <div
                        className={`rounded-md px-4 py-2.5 ${isLeft ? 'bg-green-50' : 'bg-blue-50'} border`}
                      >
                        <div className="space-y-1.5">
                          <div className="h-2 bg-muted rounded w-full" />
                          <div className="h-2 bg-muted rounded w-4/5" />
                          {index % 2 === 0 && <div className="h-2 bg-muted rounded w-3/5" />}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : hasNoAssets ? (
          <div className="flex h-full items-center justify-center px-4">
            <TranscriptEmptyState
              message="No transcript available"
              description="Transcripts will appear here once generated"
            />
          </div>
        ) : hasEmptyTranscript ? (
          <div className="flex h-full items-center justify-center px-4">
            <TranscriptEmptyState message="No transcript found" />
          </div>
        ) : (
          <div className="px-4">
            {transcript.map((segment, index) => {
              const originalIndex = segmentToOriginalIndex.get(segment) ?? index;
              const isInHighlightRange = highlightedSegmentIndices.has(originalIndex);
              const isActive =
                isInHighlightRange || (!evidenceHighlightRange && segment === activeSegment);
              const speaker = segment.speaker || 'System';
              const isHuman = speaker === 'Human' || speaker === 'Reporter';
              const isAssistant = speaker === 'Assistant';
              const previousSpeaker = index > 0 ? transcript[index - 1].speaker : null;
              const wasPreviousSame = previousSpeaker === speaker && index > 0;
              const segmentMatches =
                transcriptSearchState.matchesBySegment.get(originalIndex) ?? [];
              const globalMatchStartIndex =
                transcriptSearchState.globalMatchStartBySegment.get(originalIndex) ?? 0;

              const alignment = isHuman ? 'justify-start' : 'justify-end';

              const getBackgroundColor = () => {
                if (isActive) {
                  if (isHuman) return 'bg-green-600 text-primary-foreground';
                  if (isAssistant) return 'bg-blue-600 text-primary-foreground';
                  return 'bg-muted-foreground text-primary-foreground';
                }
                if (isHuman) return 'bg-green-50 hover:bg-green-100 border';
                if (isAssistant) return 'bg-blue-50 hover:bg-blue-100 border';
                return 'bg-muted hover:bg-muted/80 border';
              };

              const getSpeakerColor = () => {
                if (isActive) {
                  if (isHuman) return 'text-green-700 font-semibold';
                  if (isAssistant) return 'text-blue-700 font-semibold';
                  return 'text-foreground font-semibold';
                }
                if (isHuman) return 'text-green-700';
                if (isAssistant) return 'text-blue-700';
                return 'text-foreground';
              };

              return (
                <div
                  key={index}
                  ref={
                    isInHighlightRange && originalIndex === firstHighlightedIndex
                      ? firstHighlightedRef
                      : isActive
                        ? activeSegmentRef
                        : index === transcript.length - 1
                          ? lastSegmentRef
                          : null
                  }
                  className={`flex ${alignment} mb-2`}
                  onClick={() => {
                    onSegmentClick?.(segment.start);
                    onEvidenceHighlightClear?.();
                  }}
                >
                  <div className="group cursor-pointer max-w-[85%]">
                    <div
                      className={`flex items-center gap-2 mb-1 ${alignment === 'justify-start' ? '' : 'justify-end'}`}
                    >
                      {!wasPreviousSame && (
                        <span className={`text-xs font-medium ${getSpeakerColor()}`}>
                          {speaker}
                        </span>
                      )}
                    </div>
                    <div
                      className={`flex items-end gap-1 ${alignment === 'justify-start' ? 'flex-row-reverse' : ''}`}
                    >
                      <span className="text-xs mb-0.5 text-muted-foreground">
                        {formatDuration(segment.start)}
                      </span>
                      <div
                        className={`rounded-md px-2 py-1 transition-all ${getBackgroundColor()}`}
                      >
                        <p
                          className={`text-sm leading-relaxed break-words ${
                            isActive ? 'text-primary-foreground' : 'text-foreground'
                          }`}
                        >
                          {renderSearchMatches({
                            text: segment.text,
                            matches: segmentMatches,
                            globalMatchStartIndex,
                            activeSearchMatchIndex: normalizedActiveSearchMatchIndex,
                            getMatchRef: getSearchMatchRef,
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
